import { Box, Button, Drawer, GridLegacy as Grid, MenuItem, Paper, Select, Stack, Tab, Tabs, TextField, Typography, Divider, IconButton } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import CloseFullscreenIcon from "@mui/icons-material/CloseFullscreen";
import { useCallback, useState, useEffect, useRef } from "react";
import { useDrag, useDrop } from "react-dnd";
import update from "immutability-helper";
import { v4 as uuidv4 } from "uuid";
import TextBlockEditor from "./pageBlocks/TextBlockEditor";
import ImageBlockEditor from "./pageBlocks/ImageBlockEditor";
import ButtonBlockEditor from "./pageBlocks/ButtonBlockEditor";
import LinkBlockEditor from "./pageBlocks/LinkBlockEditor";
import GridBlockEditor from "./pageBlocks/GridBlockEditor";
import SectionBlockEditor from "./pageBlocks/SectionBlockEditor";
import HeroBlockEditor from "./pageBlocks/HeroBlockEditor";
import CardBlockEditor from "./pageBlocks/CardBlockEditor";
import VideoBlockEditor from "./pageBlocks/VideoBlockEditor";
import SpacerBlockEditor from "./pageBlocks/SpacerBlockEditor";
import DividerBlockEditor from "./pageBlocks/DividerBlockEditor";
import CTABlockEditor from "./pageBlocks/CTABlockEditor";
import TestimonialBlockEditor from "./pageBlocks/TestimonialBlockEditor";
import AccordionBlockEditor from "./pageBlocks/AccordionBlockEditor";
import { updatePage } from "../api/page";
import { useNavigate } from "react-router-dom";

interface Props {
    page: Page;
    onSave: () => void;
}

interface SortableBlockProps {
    block: PageBlock;
    index: number;
    onUpdate: (id: string, content: any) => void;
    onDelete: (id: string) => void;
    moveBlock: (dragIndex: number, hoverIndex: number) => void;
}

interface DragItem {
    index: number;
    id: string;
    type: string;
}

interface CollectedProps {
    handlerId: any;
}

const SortableBlock = ({ block, index, onUpdate, onDelete, moveBlock }: SortableBlockProps) => {
    const ref = useRef<HTMLDivElement>(null);

    const [{ handlerId }, drop] = useDrop<DragItem, void, CollectedProps>({
        accept: "BLOCK",
        collect(monitor) {
            return {
                handlerId: monitor.getHandlerId(),
            };
        },
        hover(item: DragItem, monitor) {
            if (!ref.current) {
                return;
            }
            const dragIndex = item.index;
            const hoverIndex = index;

            if (dragIndex === hoverIndex) {
                return;
            }

            const hoverBoundingRect = ref.current?.getBoundingClientRect();
            const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset!.y - hoverBoundingRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
                return;
            }

            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
                return;
            }

            moveBlock(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag] = useDrag<DragItem, void, { isDragging: boolean }>({
        type: "BLOCK",
        item: () => {
            return { id: block.id, index, type: "BLOCK" };
        },
        collect: (monitor) => ({
            isDragging: monitor.isDragging(),
        }),
    });

    const opacity = isDragging ? 0.4 : 1;
    drag(drop(ref));

    const renderBlock = () => {
        const commonProps = {
            onChange: (content: any) => onUpdate(block.id, content),
            onDelete: () => onDelete(block.id),
        };

        switch (block.type) {
            case PageBlockType.TEXT:
                return <TextBlockEditor block={block as TextBlock} {...commonProps} />;
            case PageBlockType.IMAGE:
                return <ImageBlockEditor block={block as ImageBlock} {...commonProps} />;
            case PageBlockType.BUTTON:
                return <ButtonBlockEditor block={block as ButtonBlock} {...commonProps} />;
            case PageBlockType.LINK:
                return <LinkBlockEditor block={block as LinkBlock} {...commonProps} />;
            case PageBlockType.GRID:
                return <GridBlockEditor block={block as GridBlock} {...commonProps} />;
            case PageBlockType.SECTION:
                return <SectionBlockEditor block={block as SectionBlock} {...commonProps} />;
            case PageBlockType.HERO:
                return <HeroBlockEditor block={block as HeroBlock} {...commonProps} />;
            case PageBlockType.CARD:
                return <CardBlockEditor block={block as CardBlock} {...commonProps} />;
            case PageBlockType.VIDEO:
                return <VideoBlockEditor block={block as VideoBlock} {...commonProps} />;
            case PageBlockType.SPACER:
                return <SpacerBlockEditor block={block as SpacerBlock} {...commonProps} />;
            case PageBlockType.DIVIDER:
                return <DividerBlockEditor block={block as DividerBlock} {...commonProps} />;
            case PageBlockType.CTA:
                return <CTABlockEditor block={block as CTABlock} {...commonProps} />;
            case PageBlockType.TESTIMONIAL:
                return <TestimonialBlockEditor block={block as TestimonialBlock} {...commonProps} />;
            case PageBlockType.ACCORDION:
                return <AccordionBlockEditor block={block as AccordionBlock} {...commonProps} />;
            default:
                return null;
        }
    };

    return (
        <div ref={ref} style={{ opacity }} data-handler-id={handlerId}>
            {renderBlock()}
        </div>
    );
};

const FullscreenPageBuilder = ({ page, onSave }: Props) => {
    const [blocks, setBlocks] = useState<PageBlock[]>([]);
    const [title, setTitle] = useState(page.title);
    const [slug, setSlug] = useState(page.slug);
    const [navHighlight, setNavHighlight] = useState(page.navHighlight);
    const [navOrder, setNavOrder] = useState(page.navOrder);
    const [live, setLive] = useState(page.live);
    const [seoTitle, setSeoTitle] = useState(page.seoTitle || "");
    const [seoDescription, setSeoDescription] = useState(page.seoDescription || "");
    const [seoKeywords, setSeoKeywords] = useState(page.seoKeywords || "");
    const [seoImage, setSeoImage] = useState(page.seoImage || "");
    const [saving, setSaving] = useState(false);
    const [sidebarTab, setSidebarTab] = useState(0);
    const [sidebarOpen, setSidebarOpen] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        if (page.blocks) {
            try {
                const parsedBlocks = JSON.parse(page.blocks);
                setBlocks(parsedBlocks);
            } catch (e) {
                console.error("Failed to parse blocks:", e);
                setBlocks([]);
            }
        } else {
            setBlocks([]);
        }
    }, [page.blocks]);

    const moveBlock = useCallback((dragIndex: number, hoverIndex: number) => {
        setBlocks((prevBlocks) =>
            update(prevBlocks, {
                $splice: [
                    [dragIndex, 1],
                    [hoverIndex, 0, prevBlocks[dragIndex]],
                ],
            })
        );
    }, []);

    const addBlock = useCallback((type: PageBlockType) => {
        const newBlock: PageBlock = {
            id: uuidv4(),
            type,
            order: blocks.length,
            content: getDefaultContent(type),
        };
        setBlocks([...blocks, newBlock]);
    }, [blocks]);

    const getDefaultContent = (type: PageBlockType) => {
        switch (type) {
            case PageBlockType.TEXT:
                return { html: "<p>Enter text here...</p>" };
            case PageBlockType.IMAGE:
                return { url: "", alt: "", caption: "" };
            case PageBlockType.BUTTON:
                return { text: "Click me", url: "", variant: "contained", color: "primary" };
            case PageBlockType.LINK:
                return { text: "Link", url: "" };
            case PageBlockType.GRID:
                return { columns: 2, blocks: [[], []] };
            case PageBlockType.SECTION:
                return { title: "", backgroundColor: "", blocks: [] };
            case PageBlockType.HERO:
                return { title: "Hero Title", subtitle: "Hero subtitle", height: "400px", textAlign: "center" };
            case PageBlockType.CARD:
                return { title: "Card Title", description: "", imagePosition: "top" };
            case PageBlockType.VIDEO:
                return { url: "", caption: "", autoplay: false };
            case PageBlockType.SPACER:
                return { height: "50px" };
            case PageBlockType.DIVIDER:
                return { thickness: "1px", color: "#cccccc", style: "solid" };
            case PageBlockType.CTA:
                return { title: "Take Action", buttonText: "Get Started", buttonUrl: "" };
            case PageBlockType.TESTIMONIAL:
                return { quote: "", author: "" };
            case PageBlockType.ACCORDION:
                return { items: [{ title: "Item 1", content: "" }] };
            default:
                return {};
        }
    };

    const updateBlock = useCallback((id: string, content: any) => {
        setBlocks((prevBlocks) =>
            prevBlocks.map((block) =>
                block.id === id ? { ...block, content } : block
            )
        );
    }, []);

    const deleteBlock = useCallback((id: string) => {
        setBlocks((prevBlocks) => prevBlocks.filter((block) => block.id !== id));
    }, []);

    const handleSave = async () => {
        setSaving(true);
        try {
            await updatePage(page.id, {
                title,
                slug,
                content: page.content,
                blocks: JSON.stringify(blocks),
                navHighlight,
                navOrder,
                live,
                seoTitle: seoTitle || undefined,
                seoDescription: seoDescription || undefined,
                seoKeywords: seoKeywords || undefined,
                seoImage: seoImage || undefined,
            });
            onSave();
        } catch (error) {
            console.error("Failed to save page:", error);
        } finally {
            setSaving(false);
        }
    };

    const blockCategories = [
        {
            name: "Basic",
            blocks: [
                { type: PageBlockType.TEXT, label: "Text", icon: "📝" },
                { type: PageBlockType.IMAGE, label: "Image", icon: "🖼️" },
                { type: PageBlockType.BUTTON, label: "Button", icon: "🔘" },
                { type: PageBlockType.LINK, label: "Link", icon: "🔗" },
            ],
        },
        {
            name: "Layout",
            blocks: [
                { type: PageBlockType.GRID, label: "Grid", icon: "▦" },
                { type: PageBlockType.SECTION, label: "Section", icon: "📦" },
                { type: PageBlockType.SPACER, label: "Spacer", icon: "↕️" },
                { type: PageBlockType.DIVIDER, label: "Divider", icon: "—" },
            ],
        },
        {
            name: "Content",
            blocks: [
                { type: PageBlockType.HERO, label: "Hero", icon: "🎯" },
                { type: PageBlockType.CARD, label: "Card", icon: "🃏" },
                { type: PageBlockType.VIDEO, label: "Video", icon: "🎥" },
                { type: PageBlockType.ACCORDION, label: "Accordion", icon: "📋" },
            ],
        },
        {
            name: "Marketing",
            blocks: [
                { type: PageBlockType.CTA, label: "Call-to-Action", icon: "📣" },
                { type: PageBlockType.TESTIMONIAL, label: "Testimonial", icon: "💬" },
            ],
        },
    ];

    return (
        <Box sx={{ display: "flex", height: "100vh", overflow: "hidden", bgcolor: "#f5f5f5" }}>
            {/* Main Content Area */}
            <Box sx={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden" }}>
                {/* Top Bar */}
                <Box sx={{ p: 2, bgcolor: "white", borderBottom: "1px solid #e0e0e0", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                    <Stack direction="row" spacing={2} alignItems="center">
                        <IconButton onClick={() => navigate("/admin/pages")} size="small">
                            <CloseFullscreenIcon />
                        </IconButton>
                        <Typography variant="h6">
                            {title || "Untitled Page"}
                        </Typography>
                    </Stack>
                    <Stack direction="row" spacing={2}>
                        <Button
                            variant="outlined"
                            startIcon={<SettingsIcon />}
                            onClick={() => {
                                setSidebarTab(0);
                                setSidebarOpen(true);
                            }}
                        >
                            Settings
                        </Button>
                        <Button
                            variant="outlined"
                            startIcon={<ViewModuleIcon />}
                            onClick={() => {
                                setSidebarTab(1);
                                setSidebarOpen(true);
                            }}
                        >
                            Blocks
                        </Button>
                        <Button
                            variant="contained"
                            startIcon={<SaveIcon />}
                            onClick={handleSave}
                            disabled={saving}
                        >
                            {saving ? "Saving..." : "Save"}
                        </Button>
                    </Stack>
                </Box>

                {/* Canvas Area */}
                <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
                    <Box sx={{ maxWidth: 1200, mx: "auto" }}>
                        {blocks.map((block, index) => (
                            <SortableBlock
                                key={block.id}
                                block={block}
                                index={index}
                                onUpdate={updateBlock}
                                onDelete={deleteBlock}
                                moveBlock={moveBlock}
                            />
                        ))}
                        {blocks.length === 0 && (
                            <Paper sx={{ p: 6, textAlign: "center", bgcolor: "white" }}>
                                <Typography variant="h5" color="text.secondary" gutterBottom>
                                    Start Building Your Page
                                </Typography>
                                <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                                    Click on "Blocks" to add your first content block
                                </Typography>
                                <Button
                                    variant="contained"
                                    startIcon={<ViewModuleIcon />}
                                    onClick={() => {
                                        setSidebarTab(1);
                                        setSidebarOpen(true);
                                    }}
                                >
                                    Open Blocks Panel
                                </Button>
                            </Paper>
                        )}
                    </Box>
                </Box>
            </Box>

            {/* Right Sidebar */}
            <Drawer
                anchor="right"
                open={sidebarOpen}
                onClose={() => setSidebarOpen(false)}
                variant="persistent"
                sx={{
                    width: 350,
                    flexShrink: 0,
                    "& .MuiDrawer-paper": {
                        width: 350,
                        boxSizing: "border-box",
                    },
                }}
            >
                <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
                    <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
                        <Tabs value={sidebarTab} onChange={(e, v) => setSidebarTab(v)}>
                            <Tab label="Settings" />
                            <Tab label="Blocks" />
                        </Tabs>
                    </Box>

                    <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
                        {sidebarTab === 0 && (
                            <Stack spacing={2}>
                                <Typography variant="h6">Page Settings</Typography>
                                <TextField
                                    label="Title"
                                    value={title}
                                    onChange={(e) => setTitle(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="Slug"
                                    value={slug}
                                    onChange={(e) => setSlug(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="Nav Highlight"
                                    value={navHighlight}
                                    onChange={(e) => setNavHighlight(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="Nav Order"
                                    type="number"
                                    value={navOrder}
                                    onChange={(e) => setNavOrder(parseInt(e.target.value))}
                                    fullWidth
                                    size="small"
                                />
                                <Stack direction="row" spacing={2} alignItems="center">
                                    <Typography variant="body2">Status:</Typography>
                                    <Select
                                        value={live ? "published" : "draft"}
                                        onChange={(e) => setLive(e.target.value === "published")}
                                        size="small"
                                        fullWidth
                                    >
                                        <MenuItem value="draft">Draft</MenuItem>
                                        <MenuItem value="published">Published</MenuItem>
                                    </Select>
                                </Stack>
                                
                                <Divider sx={{ my: 2 }} />
                                
                                <Typography variant="h6">SEO Settings</Typography>
                                <TextField
                                    label="SEO Title"
                                    value={seoTitle}
                                    onChange={(e) => setSeoTitle(e.target.value)}
                                    fullWidth
                                    size="small"
                                    helperText="Leave empty to use page title"
                                />
                                <TextField
                                    label="SEO Description"
                                    value={seoDescription}
                                    onChange={(e) => setSeoDescription(e.target.value)}
                                    fullWidth
                                    size="small"
                                    multiline
                                    rows={3}
                                />
                                <TextField
                                    label="SEO Keywords"
                                    value={seoKeywords}
                                    onChange={(e) => setSeoKeywords(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                                <TextField
                                    label="SEO Image URL"
                                    value={seoImage}
                                    onChange={(e) => setSeoImage(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </Stack>
                        )}

                        {sidebarTab === 1 && (
                            <Stack spacing={3}>
                                {blockCategories.map((category) => (
                                    <Box key={category.name}>
                                        <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: "bold", color: "text.secondary" }}>
                                            {category.name}
                                        </Typography>
                                        <Grid container spacing={1}>
                                            {category.blocks.map((blockDef) => (
                                                <Grid item xs={6} key={blockDef.type}>
                                                    <Paper
                                                        sx={{
                                                            p: 2,
                                                            textAlign: "center",
                                                            cursor: "pointer",
                                                            transition: "all 0.2s",
                                                            "&:hover": {
                                                                bgcolor: "primary.light",
                                                                color: "white",
                                                                transform: "translateY(-2px)",
                                                                boxShadow: 2,
                                                            },
                                                        }}
                                                        onClick={() => addBlock(blockDef.type)}
                                                    >
                                                        <Typography variant="h6">{blockDef.icon}</Typography>
                                                        <Typography variant="caption" sx={{ display: "block", mt: 0.5 }}>
                                                            {blockDef.label}
                                                        </Typography>
                                                    </Paper>
                                                </Grid>
                                            ))}
                                        </Grid>
                                    </Box>
                                ))}
                            </Stack>
                        )}
                    </Box>
                </Box>
            </Drawer>
        </Box>
    );
};

export default FullscreenPageBuilder;
